import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { LayoutDocument } from '../lib/form-layout/types';
import type { TractFormFieldName } from '../lib/form-layout/tract-fields';

export type TractLayoutDocument = LayoutDocument<TractFormFieldName>;

export interface ResolvedLayout {
  layout: TractLayoutDocument;
  scope: 'global' | 'project' | 'default';
  resolved_from: 'project' | 'global' | 'none';
  source_id: string | null;
  updated_at: string | null;
  notes: string | null;
}

export const tractFormLayoutKeys = {
  all: ['tract-form-layout'] as const,
  active: (projectId: string | null) => [...tractFormLayoutKeys.all, 'active', projectId] as const,
  adminActive: (scope: 'global' | 'project', projectId: string | null) =>
    [...tractFormLayoutKeys.all, 'admin', 'active', scope, projectId] as const,
};

export function useTractFormLayout(projectId: string | null) {
  return useQuery({
    queryKey: tractFormLayoutKeys.active(projectId),
    staleTime: 60_000,
    queryFn: () =>
      api.get<ResolvedLayout>(
        '/tract-form-layout',
        projectId ? { project_id: projectId } : undefined,
      ),
  });
}

export function useAdminActiveTractLayout(
  scope: 'global' | 'project',
  projectId: string | null,
) {
  return useQuery({
    queryKey: tractFormLayoutKeys.adminActive(scope, projectId),
    queryFn: () => {
      const q: Record<string, string> = { scope };
      if (scope === 'project' && projectId) q.project_id = projectId;
      return api.get<ResolvedLayout>('/admin/tract-form-layout/active', q);
    },
    enabled: scope === 'global' || projectId !== null,
  });
}

export interface UpdateTractLayoutInput {
  scope: 'global' | 'project';
  project_id?: string | null;
  layout: TractLayoutDocument;
  notes?: string | null;
}

export function useUpdateTractFormLayout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTractLayoutInput) =>
      api.put<ResolvedLayout>('/admin/tract-form-layout', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tractFormLayoutKeys.all }),
  });
}
